/**
 * The v2 newsletter-setup wizard: eight tap-first screens that take a cold
 * visitor to a saved-and-signed-up newsletter with near-zero required typing.
 *
 * Architecture (unchanged from v1): every generative step talks to a
 * `SuggestionProvider` (deterministic mock today, real endpoints later) through
 * the small `useSuggestion` async hook, so swapping in network calls needs no UI
 * rework. All wizard state is the pure `NewsletterDraft` from `./wizard/draft`,
 * mirrored to localStorage on every change (see `./wizard/storage`) so nothing
 * the user enters is ever lost.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { validateSubdomain } from '@nuff/core';
import { Button, Field, Notification } from '@nuff/ui/react';

import { useAuth } from './auth/context';
import { CodeInput } from './auth/CodeInput';
import { friendlyAuthMessage, isUsernameExists, PASSWORD_HINT } from './auth/errors';

import {
  addFocus,
  addLiked,
  audienceFor,
  canAdvance,
  createDraft,
  furthestStep,
  isLiked,
  LAST_STEP,
  MAX_FOCUS,
  parseLink,
  recapLine,
  removeLiked,
  seedSignature,
  setCustomVoice,
  setName,
  setPurposeKind,
  setPurposeText,
  setSubdomain,
  setVoicePreset,
  STEP,
  STEPS,
  subdomainCandidates,
  toggleFocus,
  type DraftPurpose,
  type NewsletterDraft,
} from './wizard/draft';
import { POPULAR_NEWSLETTERS, sampleNewsletters, sampleTopics } from './wizard/presets';
import { clearDraft, loadCurrentDraft, saveDraft } from './wizard/storage';
import type {
  NameSuggestion,
  SubdomainAvailability,
  SuggestionInput,
  SuggestionProvider,
  VoiceOption,
} from './suggestions/provider';
import './Wizard.css';

/* ----------------------------------------------------------- async hook -- */

type Status = 'loading' | 'ready' | 'error';

interface AsyncResult<T> {
  readonly status: Status;
  readonly data: T | undefined;
  readonly error: unknown;
  readonly reload: () => void;
}

/** Give up on a suggestion request after this long. */
const REQUEST_TIMEOUT_MS = 15000;

/** Run an async loader with loading/error state; aborts in-flight on change. */
function useSuggestion<T>(load: (signal: AbortSignal) => Promise<T>, key: string): AsyncResult<T> {
  const [state, setState] = useState<{ status: Status; data: T | undefined; error: unknown }>({
    status: 'loading',
    data: undefined,
    error: undefined,
  });
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort(new DOMException('The request timed out', 'TimeoutError'));
    }, REQUEST_TIMEOUT_MS);

    setState({ status: 'loading', data: undefined, error: undefined });
    loadRef
      .current(controller.signal)
      .then(
        (result) => {
          if (!cancelled) {
            setState({ status: 'ready', data: result, error: undefined });
          }
        },
        (error: unknown) => {
          if (!cancelled) {
            setState({ status: 'error', data: undefined, error });
          }
        },
      )
      .finally(() => {
        clearTimeout(timeout);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [key, nonce]);

  return {
    ...state,
    reload: () => {
      setNonce((value) => value + 1);
    },
  };
}

/* ----------------------------------------------------------- presenters -- */

function Skeleton({ rows = 3, label }: { rows?: number; label: string }): React.JSX.Element {
  return (
    <div className="wizard__loading" role="status" aria-live="polite" aria-busy="true">
      <span className="wizard__loading-label">{label}</span>
      {Array.from({ length: rows }).map((_, index) => (
        <span key={index} className="wizard__skeleton" aria-hidden="true" />
      ))}
    </div>
  );
}

function ErrorRetry({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  return (
    <div className="wizard__error" role="alert">
      <p>Our AI hit a snag. Try again?</p>
      <Button variant="ghost" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

interface AsyncBoundaryProps<T> {
  readonly result: AsyncResult<T>;
  readonly skeletonRows?: number;
  readonly loadingLabel: string;
  readonly children: (data: T) => React.JSX.Element;
}

function AsyncBoundary<T>({
  result,
  skeletonRows,
  loadingLabel,
  children,
}: AsyncBoundaryProps<T>): React.JSX.Element {
  if (result.status === 'loading') {
    return <Skeleton rows={skeletonRows ?? 3} label={loadingLabel} />;
  }
  if (result.status === 'error' || result.data === undefined) {
    return <ErrorRetry onRetry={result.reload} />;
  }
  return children(result.data);
}

/** The persistent "you can change this later" reassurance line. */
function Reassure({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <p className="wizard__reassure">{children}</p>;
}

/* ---------------------------------------------------------------- steps -- */

interface StepProps {
  readonly draft: NewsletterDraft;
  readonly setDraft: React.Dispatch<React.SetStateAction<NewsletterDraft>>;
  readonly provider: SuggestionProvider;
}

function providerInput(draft: NewsletterDraft): SuggestionInput {
  // The primary (first) focus topic seeds names and voice samples, so the copy
  // reads cleanly. All focus topics still feed discovery later via the draft.
  return { topic: draft.focus[0] ?? '', audience: audienceFor(draft) };
}

function readingLabel(draft: NewsletterDraft): string {
  const topic = draft.focus[0];
  return topic ? `Reading up on ${topic}...` : 'Reading up on your topic...';
}

/* ---- Welcome ---- */

function WelcomeStep(): React.JSX.Element {
  return (
    <div className="wizard__step">
      <p className="wizard__eyebrow">Let&rsquo;s set up your newsletter</p>
      <h1 className="wizard__title" tabIndex={-1}>
        Our AI does the work. You just pick.
      </h1>
      <p className="wizard__hint">
        About a minute. Tap to choose at each step, and change anything later.
      </p>
    </div>
  );
}

/* ---- Purpose ---- */

const PURPOSE_OPTIONS: readonly {
  readonly kind: DraftPurpose['kind'];
  readonly label: string;
  readonly blurb: string;
}[] = [
  { kind: 'company', label: 'Your company or brand', blurb: 'Keep your audience warm, on-brand.' },
  {
    kind: 'community',
    label: 'A community or your followers',
    blurb: 'Share the scene you love with people who get it.',
  },
  { kind: 'other', label: 'Something else', blurb: 'Tell us in a few words.' },
];

function PurposeStep({ draft, setDraft }: StepProps): React.JSX.Element {
  return (
    <div className="wizard__step">
      <h1 className="wizard__title" tabIndex={-1}>
        Who&rsquo;s it for?
      </h1>
      <p className="wizard__hint">This tailors every suggestion that follows.</p>
      <div className="wizard__cards" role="radiogroup" aria-label="Purpose">
        {PURPOSE_OPTIONS.map((option) => {
          const selected = draft.purpose.kind === option.kind;
          return (
            <label
              key={option.kind}
              className={selected ? 'wizard__card wizard__card--selected' : 'wizard__card'}
            >
              <input
                type="radio"
                name="purpose"
                className="wizard__radio-input"
                value={option.kind}
                checked={selected}
                onChange={() => setDraft((prev) => setPurposeKind(prev, option.kind))}
              />
              <span className="wizard__card-body">
                <span className="wizard__card-title">{option.label}</span>
                <span className="wizard__card-blurb">{option.blurb}</span>
              </span>
            </label>
          );
        })}
      </div>
      {draft.purpose.kind === 'other' && (
        <div className="wizard__add">
          <Field
            id="purpose-other"
            name="purpose-other"
            label="Who is it for?"
            appearance="underline"
            value={draft.purpose.customText}
            placeholder="e.g. my class of students, my local running club"
            autoComplete="off"
            onChange={(value) => setDraft((prev) => setPurposeText(prev, value))}
          />
        </div>
      )}
      <Reassure>You can change who it&rsquo;s for anytime.</Reassure>
    </div>
  );
}

/* ---- Focus ---- */

function FocusStep({ draft, setDraft }: StepProps): React.JSX.Element {
  const [salt, setSalt] = useState(0);
  const [entry, setEntry] = useState('');
  const sampled = useMemo(
    () => sampleTopics(`${draft.draftId}:${salt}`).map((topic) => topic.label),
    [draft.draftId, salt],
  );
  const atMax = draft.focus.length >= MAX_FOCUS;
  const inFocus = (label: string): boolean =>
    draft.focus.some((item) => item.toLowerCase() === label.toLowerCase());
  const extras = draft.focus.filter(
    (item) => !sampled.some((l) => l.toLowerCase() === item.toLowerCase()),
  );
  const chips = [...extras, ...sampled];

  function submitEntry(): void {
    setDraft((prev) => addFocus(prev, entry));
    setEntry('');
  }

  return (
    <div className="wizard__step">
      <h1 className="wizard__title" tabIndex={-1}>
        What&rsquo;s it about?
      </h1>
      <p className="wizard__hint">Tap what fits. Add your own if you don&rsquo;t see it.</p>
      <p className="wizard__counter" aria-live="polite">
        Pick 1 to 3 ({draft.focus.length} selected)
      </p>
      <div className="wizard__chips" role="group" aria-label="Topics">
        {chips.map((label) => {
          const selected = inFocus(label);
          return (
            <button
              key={label}
              type="button"
              aria-pressed={selected}
              disabled={!selected && atMax}
              className={selected ? 'wizard__chip wizard__chip--selected' : 'wizard__chip'}
              onClick={() => setDraft((prev) => toggleFocus(prev, label))}
            >
              {label}
            </button>
          );
        })}
      </div>
      {atMax && <p className="wizard__note">That&rsquo;s the max of 3. Untick one to swap.</p>}
      <Button
        variant="ghost"
        className="wizard__inline-action"
        onClick={() => setSalt((value) => value + 1)}
      >
        Show different topics
      </Button>
      <form
        className="wizard__add"
        onSubmit={(event) => {
          event.preventDefault();
          submitEntry();
        }}
      >
        <div className="wizard__add-row">
          <Field
            id="add-focus"
            name="add-focus"
            label="Add your own"
            appearance="underline"
            value={entry}
            placeholder="e.g. indie coffee roasters"
            autoComplete="off"
            disabled={atMax}
            onChange={setEntry}
          />
          <Button variant="ghost" type="submit" disabled={atMax}>
            Add
          </Button>
        </div>
      </form>
      <Reassure>Not sure yet? Pick the closest. You can refine it later.</Reassure>
    </div>
  );
}

/* ---- Voice ---- */

function VoiceStep({ draft, setDraft, provider }: StepProps): React.JSX.Element {
  const input = providerInput(draft);
  const result = useSuggestion<VoiceOption[]>(
    (signal) => provider.suggestVoices(input, { signal }),
    `voices:${seedSignature(draft)}`,
  );
  const lastPreset = useRef<{ id: string; label: string }>(
    draft.voice.kind === 'preset'
      ? { id: draft.voice.id, label: draft.voice.label }
      : { id: 'warm', label: 'Warm and plain-spoken' },
  );
  const [customText, setCustomText] = useState(
    draft.voice.kind === 'custom' ? draft.voice.text : '',
  );
  const usingCustom = draft.voice.kind === 'custom';

  return (
    <div className="wizard__step">
      <h1 className="wizard__title" tabIndex={-1}>
        Pick a voice
      </h1>
      <p className="wizard__hint">
        Pick the voice that fits. Tap a sample tone, or paste your own.
      </p>
      <AsyncBoundary result={result} skeletonRows={4} loadingLabel={readingLabel(draft)}>
        {(voices) => (
          <div className="wizard__cards" role="radiogroup" aria-label="Voice options">
            {voices.map((voice) => {
              const selected =
                !usingCustom && draft.voice.kind === 'preset' && draft.voice.id === voice.id;
              return (
                <label
                  key={voice.id}
                  className={selected ? 'wizard__card wizard__card--selected' : 'wizard__card'}
                >
                  <input
                    type="radio"
                    name="voice"
                    className="wizard__radio-input"
                    value={voice.id}
                    checked={selected}
                    onChange={() => {
                      lastPreset.current = { id: voice.id, label: voice.label };
                      setDraft((prev) => setVoicePreset(prev, voice));
                    }}
                  />
                  <span className="wizard__card-body">
                    <span className="wizard__card-title">{voice.label}</span>
                    <span className="wizard__card-blurb">&ldquo;{voice.sample}&rdquo;</span>
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </AsyncBoundary>
      <p className="wizard__divider">Already have a voice?</p>
      <Field
        id="custom-voice"
        name="custom-voice"
        label="Paste a paragraph you’ve written and our AI will match it."
        multiline
        rows={3}
        value={customText}
        placeholder="Paste a few sentences in your voice..."
        onChange={(text) => {
          setCustomText(text);
          setDraft((prev) => setCustomVoice(prev, text, lastPreset.current));
        }}
      />
      {usingCustom && <p className="wizard__note">Using your pasted voice.</p>}
      <Reassure>You can retune the voice anytime.</Reassure>
    </div>
  );
}

/* ---- Name ---- */

function NameStep({ draft, setDraft, provider }: StepProps): React.JSX.Element {
  const input = providerInput(draft);
  const result = useSuggestion<NameSuggestion[]>(
    (signal) => provider.suggestNames(input, { signal }),
    `names:${seedSignature(draft)}`,
  );
  const names = result.data;

  // Auto-select the first suggestion once the list loads, so Next is enabled by
  // default and the user can simply move on.
  useEffect(() => {
    if (names && names.length > 0) {
      setDraft((prev) => {
        if (prev.name.trim().length > 0) {
          return prev;
        }
        const first = names[0]!;
        return setName(prev, first.name, first.tagline);
      });
    }
  }, [names, setDraft]);

  return (
    <div className="wizard__step">
      <h1 className="wizard__title" tabIndex={-1}>
        Name it
      </h1>
      <p className="wizard__hint">Tap a name our AI drafted from your topic, or write your own.</p>
      <AsyncBoundary result={result} skeletonRows={4} loadingLabel="Coming up with names...">
        {(suggestions) => {
          const isSuggested = suggestions.some(
            (s) => s.name === draft.name && s.tagline === draft.tagline,
          );
          return (
            <>
              <div className="wizard__cards" role="radiogroup" aria-label="Name suggestions">
                {suggestions.map((suggestion) => {
                  const selected =
                    draft.name === suggestion.name && draft.tagline === suggestion.tagline;
                  return (
                    <label
                      key={suggestion.name}
                      className={selected ? 'wizard__card wizard__card--selected' : 'wizard__card'}
                    >
                      <input
                        type="radio"
                        name="name-option"
                        className="wizard__radio-input"
                        checked={selected}
                        onChange={() =>
                          setDraft((prev) => setName(prev, suggestion.name, suggestion.tagline))
                        }
                      />
                      <span className="wizard__card-body">
                        <span className="wizard__card-title">{suggestion.name}</span>
                        <span className="wizard__card-blurb">{suggestion.tagline}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              <Button
                variant="ghost"
                className="wizard__inline-action"
                onClick={result.reload}
              >
                Show more names
              </Button>
              <Field
                id="custom-name"
                name="custom-name"
                label="Or write your own"
                appearance="underline"
                value={isSuggested ? '' : draft.name}
                placeholder="Your newsletter name"
                autoComplete="off"
                onChange={(value) => setDraft((prev) => setName(prev, value, ''))}
              />
            </>
          );
        }}
      </AsyncBoundary>
      <Reassure>Names are easy to change later.</Reassure>
    </div>
  );
}

/* ---- Address ---- */

type Availability =
  | { status: 'idle' }
  | { status: 'invalid'; reason: string }
  | { status: 'checking' }
  | { status: 'available' }
  | { status: 'taken'; reason: string }
  | { status: 'error' };

/** Client-side validate first, then a debounced availability check. */
function useDebouncedAvailability(provider: SuggestionProvider, name: string): Availability {
  const [state, setState] = useState<Availability>({ status: 'idle' });
  useEffect(() => {
    if (name.trim().length === 0) {
      setState({ status: 'idle' });
      return;
    }
    const parsed = validateSubdomain(name);
    if (!parsed.ok) {
      setState({ status: 'invalid', reason: parsed.reason });
      return;
    }
    setState({ status: 'checking' });
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      provider
        .checkSubdomain(parsed.value, { signal: controller.signal })
        .then((result: SubdomainAvailability) => {
          if (!cancelled) {
            setState(
              result.available
                ? { status: 'available' }
                : { status: 'taken', reason: result.reason ?? 'That address is taken.' },
            );
          }
        })
        .catch(() => {
          if (!cancelled) {
            setState({ status: 'error' });
          }
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [name, provider]);
  return state;
}

interface AddressStepProps extends StepProps {
  readonly onReadyChange: (ready: boolean) => void;
}

function AddressStep({
  draft,
  setDraft,
  provider,
  onReadyChange,
}: AddressStepProps): React.JSX.Element {
  const candidates = useMemo(() => subdomainCandidates(draft.name), [draft.name]);
  const [candStatus, setCandStatus] = useState<Record<string, 'checking' | 'available' | 'taken'>>(
    {},
  );
  const availability = useDebouncedAvailability(provider, draft.subdomain);
  const autoSelected = useRef(false);

  // Check each generated candidate's availability.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setCandStatus(Object.fromEntries(candidates.map((c) => [c, 'checking'])));
    for (const candidate of candidates) {
      provider
        .checkSubdomain(candidate, { signal: controller.signal })
        .then((result) => {
          if (!cancelled) {
            setCandStatus((prev) => ({
              ...prev,
              [candidate]: result.available ? 'available' : 'taken',
            }));
          }
        })
        .catch(() => {
          /* aborted or failed: leave as checking */
        });
    }
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [candidates, provider]);

  // Auto-select the first available candidate once, so Next is enabled by
  // default. Runs a single time: clearing the field afterwards must not refill.
  useEffect(() => {
    if (autoSelected.current) {
      return;
    }
    const firstAvailable = candidates.find((c) => candStatus[c] === 'available');
    if (firstAvailable) {
      autoSelected.current = true;
      setDraft((prev) => (prev.subdomain.length > 0 ? prev : setSubdomain(prev, firstAvailable)));
    }
  }, [candStatus, candidates, setDraft]);

  // Report readiness (valid + available) up to the wizard for Next gating.
  useEffect(() => {
    onReadyChange(availability.status === 'available');
  }, [availability.status, onReadyChange]);

  return (
    <div className="wizard__step">
      <h1 className="wizard__title" tabIndex={-1}>
        Pick your address
      </h1>
      <p className="wizard__hint">
        This is where your newsletter lives. Our AI suggested a few from your name.
      </p>
      <div className="wizard__chips" role="group" aria-label="Address suggestions">
        {candidates.map((candidate) => {
          const status = candStatus[candidate] ?? 'checking';
          const selected = draft.subdomain === candidate;
          const taken = status === 'taken';
          return (
            <button
              key={candidate}
              type="button"
              aria-pressed={selected}
              disabled={taken}
              className={selected ? 'wizard__chip wizard__chip--selected' : 'wizard__chip'}
              onClick={() => setDraft((prev) => setSubdomain(prev, candidate))}
            >
              <span>{candidate}.nuffsaid.cloud</span>
              <span className="wizard__chip-status">
                {status === 'checking' ? 'checking...' : status === 'available' ? '✓' : 'taken'}
              </span>
            </button>
          );
        })}
      </div>
      <Field
        id="custom-subdomain"
        name="custom-subdomain"
        label="Or type your own"
        appearance="underline"
        value={draft.subdomain}
        placeholder="your-address"
        autoComplete="off"
        suffix=".nuffsaid.cloud"
        onChange={(value) => setDraft((prev) => setSubdomain(prev, value))}
      />
      <p className="wizard__status" aria-live="polite">
        {availability.status === 'checking' && 'Checking...'}
        {availability.status === 'available' && `${draft.subdomain}.nuffsaid.cloud is available`}
        {availability.status === 'taken' && availability.reason}
        {availability.status === 'invalid' && availability.reason}
        {availability.status === 'error' && 'Could not check that address. Try again.'}
      </p>
      <Reassure>You can point it at a custom domain or change it later.</Reassure>
    </div>
  );
}

/* ---- Liked newsletters ---- */

function LikedStep({ draft, setDraft }: StepProps): React.JSX.Element {
  const [salt, setSalt] = useState(0);
  const [entry, setEntry] = useState('');
  const [error, setError] = useState<string | null>(null);
  const sampled = useMemo(
    () => sampleNewsletters(`${draft.draftId}:${salt}`),
    [draft.draftId, salt],
  );
  const custom = draft.likedNewsletters.filter(
    (liked) => !POPULAR_NEWSLETTERS.some((preset) => preset.url === liked.url),
  );

  function submitEntry(): void {
    const parsed = parseLink(entry);
    if (!parsed) {
      setError('invalid');
      return;
    }
    setError(null);
    setDraft((prev) => addLiked(prev, parsed));
    setEntry('');
  }

  return (
    <div className="wizard__step">
      <h1 className="wizard__title" tabIndex={-1}>
        Any newsletters you love?
      </h1>
      <p className="wizard__hint">
        Optional. It helps our AI learn the style and find great sources. Pick any, or add a link.
      </p>
      <div className="wizard__cards" aria-label="Popular newsletters">
        {sampled.map((preset) => {
          const checked = isLiked(draft, preset.url);
          return (
            <label
              key={preset.id}
              className={checked ? 'wizard__card wizard__card--selected' : 'wizard__card'}
            >
              <input
                type="checkbox"
                className="wizard__radio-input"
                checked={checked}
                onChange={() =>
                  setDraft((prev) =>
                    checked
                      ? removeLiked(prev, preset.url)
                      : addLiked(prev, { id: preset.id, label: preset.name, url: preset.url }),
                  )
                }
              />
              <span className="wizard__card-body">
                <span className="wizard__card-title">{preset.name}</span>
                <span className="wizard__card-blurb">{preset.blurb}</span>
                <span className="wizard__card-host">{preset.host}</span>
              </span>
            </label>
          );
        })}
      </div>
      <Button
        variant="ghost"
        className="wizard__inline-action"
        onClick={() => setSalt((value) => value + 1)}
      >
        Show different ones
      </Button>
      {custom.length > 0 && (
        <fieldset className="wizard__group">
          <legend className="wizard__group-title">Added by you</legend>
          {custom.map((liked) => (
            <div key={liked.url} className="wizard__switch">
              <span className="wizard__section-text">
                <span className="wizard__section-title">{liked.label}</span>
                <span className="wizard__section-desc">{liked.url}</span>
              </span>
              <button
                type="button"
                className="wizard__icon-btn"
                aria-label={`Remove ${liked.label}`}
                onClick={() => setDraft((prev) => removeLiked(prev, liked.url))}
              >
                ✕
              </button>
            </div>
          ))}
        </fieldset>
      )}
      <form
        className="wizard__add"
        onSubmit={(event) => {
          event.preventDefault();
          submitEntry();
        }}
      >
        <div className="wizard__add-row">
          <Field
            id="add-liked"
            name="add-liked"
            label="Paste a link to one you love"
            appearance="underline"
            value={entry}
            placeholder="https://..."
            autoComplete="off"
            onChange={setEntry}
          />
          <Button variant="ghost" type="submit">
            Add
          </Button>
        </div>
        {error !== null && (
          <p className="wizard__note" role="alert">
            That doesn&rsquo;t look like a link.
          </p>
        )}
      </form>
      <Reassure>Add or remove these anytime.</Reassure>
    </div>
  );
}

/* ---- Building + signup gate ---- */

function buildingMessages(draft: NewsletterDraft): string[] {
  const topic = draft.focus[0] ?? 'your topic';
  // HONEST: no work runs until the account is saved, so these describe saving the
  // setup, not sources being fetched or an issue being written (that happens after
  // signup, in the studio). Kept warm + specific to the chosen topic.
  return [
    'Saving your setup...',
    `Noting your focus on ${topic}...`,
    'Keeping your chosen voice...',
    'Getting your studio ready...',
  ];
}

interface InlineSignupProps {
  readonly draft: NewsletterDraft;
  readonly onEdit: (step: number) => void;
}

/**
 * The gate for a visitor who is ALREADY signed in (e.g. creating another
 * newsletter from the dashboard): no signup, just save the draft to the account.
 */
function SignedInGate({ draft, onEdit }: InlineSignupProps): React.JSX.Element {
  return (
    <div className="wizard__step">
      <p className="wizard__eyebrow">Almost there</p>
      <h1 className="wizard__title" tabIndex={-1}>
        You&rsquo;re all set. Save it to your account.
      </h1>
      <GateRecap draft={draft} onEdit={onEdit} />
      <Link className="nuff-btn nuff-btn--primary wizard__gate-cta" to="/app">
        Save and finish
      </Link>
      <Reassure>You can still change every detail later.</Reassure>
    </div>
  );
}

/** The animated "saving your setup" banner shown while the user registers. */
function BuildingBanner({ draft }: { draft: NewsletterDraft }): React.JSX.Element {
  const messages = useMemo(() => buildingMessages(draft), [draft]);
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((value) => (value + 1) % messages.length);
    }, 2200);
    return () => clearInterval(timer);
  }, [messages.length]);

  return (
    <>
      <div className="wizard__robots" aria-hidden="true">
        <span className="wizard__robot" />
        <span className="wizard__robot" />
        <span className="wizard__robot" />
      </div>
      <p className="wizard__building-status" role="status" aria-live="polite">
        {messages[index]}
      </p>
    </>
  );
}

/** The "here's what we'll keep" recap, with per-row jump-back Edit buttons. */
function GateRecap({ draft, onEdit }: InlineSignupProps): React.JSX.Element {
  const voiceLabel = draft.voice.kind === 'preset' ? draft.voice.label : 'Your pasted voice';
  return (
    <div className="wizard__recap">
      <RecapRow
        label="Name"
        value={draft.name || 'Your newsletter'}
        onEdit={() => onEdit(STEP.name)}
      />
      <RecapRow
        label="Address"
        value={draft.subdomain ? `${draft.subdomain}.nuffsaid.cloud` : 'Not set'}
        onEdit={() => onEdit(STEP.address)}
      />
      <RecapRow
        label="Focus"
        value={draft.focus.join(', ') || 'Not set'}
        onEdit={() => onEdit(STEP.focus)}
      />
      <RecapRow label="Voice" value={voiceLabel} onEdit={() => onEdit(STEP.voice)} />
    </div>
  );
}

type GatePhase = 'register' | 'confirm';

/**
 * The inline signup gate. Registers the visitor with Cognito, confirms the
 * emailed code inline (no context switch to a separate page), signs them in, and
 * drops them into the app with their draft intact. Recovers unfinished signups
 * and speaks Cognito's errors in our voice.
 */
function InlineSignup({ draft, onEdit }: InlineSignupProps): React.JSX.Element {
  const { signUp, confirm, resendCode, signIn } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<GatePhase>('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [existingAccount, setExistingAccount] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleRegister(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setExistingAccount(false);
    setSubmitting(true);
    try {
      await signUp(email, password);
      setPhase('confirm');
      setNotice(`We sent a code to ${email}.`);
    } catch (caught) {
      if (isUsernameExists(caught)) {
        // The email is taken. If the account is still unconfirmed, Cognito lets
        // us resend a code so they can finish. If that resend is rejected, the
        // account is already confirmed and they should sign in instead.
        try {
          await resendCode(email);
          setPhase('confirm');
          setNotice(`Looks like you already started. We sent a fresh code to ${email}.`);
        } catch {
          setError('You already have an account. Sign in instead.');
          setExistingAccount(true);
        }
      } else {
        setError(friendlyAuthMessage(caught, 'Could not create the account.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm(fullCode: string): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      await confirm(email, fullCode);
      await signIn(email, password);
      await navigate('/app');
    } catch (caught) {
      setError(friendlyAuthMessage(caught, 'Could not confirm the code.'));
      setCode('');
      setSubmitting(false);
    }
  }

  async function handleResend(): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await resendCode(email);
      setNotice(`Code sent. Check ${email}.`);
    } catch (caught) {
      setError(friendlyAuthMessage(caught, 'Could not resend the code.'));
    }
  }

  if (phase === 'confirm') {
    return (
      <div className="wizard__step">
        <p className="wizard__eyebrow">Almost there</p>
        <h1 className="wizard__title" tabIndex={-1}>
          Check your email
        </h1>
        <p className="wizard__hint">
          We sent a 6-digit code to {email}. Enter it to finish and open your newsletter.
        </p>

        <GateRecap draft={draft} onEdit={onEdit} />

        {notice !== null && <Notification variant="info">{notice}</Notification>}
        {error !== null && <Notification variant="error">{error}</Notification>}

        <label className="wizard__field-label" htmlFor="gate-code">
          Verification code
        </label>
        <CodeInput
          label="Verification code"
          value={code}
          onChange={setCode}
          onComplete={(full) => void handleConfirm(full)}
          disabled={submitting}
          autoFocus
        />

        <p className="wizard__alt">
          Didn&rsquo;t get it?{' '}
          <button
            type="button"
            className="wizard__linkbtn"
            onClick={() => void handleResend()}
            disabled={submitting}
          >
            Resend code
          </button>
        </p>
        <Reassure>You can still change every detail once you&rsquo;re in.</Reassure>
      </div>
    );
  }

  return (
    <div className="wizard__step">
      <BuildingBanner draft={draft} />
      <p className="wizard__eyebrow">Saving your setup</p>
      <h1 className="wizard__title" tabIndex={-1}>
        Create your account to keep it.
      </h1>
      <p className="wizard__hint">
        Everything you picked is saved to your account the moment you sign up. Nothing is lost.
      </p>

      <GateRecap draft={draft} onEdit={onEdit} />

      <form className="wizard__signup" onSubmit={(event) => void handleRegister(event)} noValidate>
        {error !== null && (
          <Notification variant="error">
            {error}{' '}
            {existingAccount && (
              <Link className="wizard__error-link" to="/signin">
                Sign in
              </Link>
            )}
          </Notification>
        )}
        <Field
          id="gate-email"
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={setEmail}
        />
        <Field
          id="gate-password"
          name="password"
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          hint={PASSWORD_HINT}
          value={password}
          onChange={setPassword}
        />
        <Button variant="primary" type="submit" disabled={submitting}>
          {submitting ? 'Saving...' : 'Sign up to save and continue'}
        </Button>
      </form>

      <p className="wizard__alt">
        Already have an account? <Link to="/signin">Sign in</Link>
      </p>
      <p className="wizard__note">
        Your setup is safe in this browser. Signing up saves it to your account, and nothing you
        entered is lost. {recapLine(draft)}.
      </p>
      <Reassure>You can still change every detail after you sign in.</Reassure>
    </div>
  );
}

function RecapRow({
  label,
  value,
  onEdit,
}: {
  label: string;
  value: string;
  onEdit: () => void;
}): React.JSX.Element {
  return (
    <div className="wizard__recap-row">
      <span className="wizard__recap-label">{label}</span>
      <span className="wizard__recap-value">{value}</span>
      <button type="button" className="wizard__recap-edit" onClick={onEdit}>
        Edit
      </button>
    </div>
  );
}

/* -------------------------------------------------------------- wizard -- */

export interface WizardProps {
  /** The suggestions backend, injected at the composition root. */
  readonly provider: SuggestionProvider;
}

export function Wizard({ provider }: WizardProps): React.JSX.Element {
  const { status } = useAuth();
  const signedIn = status === 'signedIn';
  // Resume a saved draft (and jump to the furthest completed step) or start fresh.
  const [draft, setDraft] = useState<NewsletterDraft>(() => loadCurrentDraft() ?? createDraft());
  const [step, setStep] = useState<number>(() => {
    const resumed = loadCurrentDraft();
    return resumed ? furthestStep(resumed) : STEP.welcome;
  });
  const [addressReady, setAddressReady] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  // Mirror the draft to localStorage, debounced, so nothing is ever lost.
  useEffect(() => {
    const timer = setTimeout(() => {
      saveDraft(draft);
    }, 300);
    return () => clearTimeout(timer);
  }, [draft]);

  const isGate = step === STEP.building;
  const stepGateReady = step === STEP.address ? addressReady : true;
  const canGoNext = canAdvance(step, draft) && stepGateReady;

  // Move focus to the step heading on each change (not the initial mount).
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const heading = titleRef.current?.querySelector<HTMLElement>('.wizard__title');
    heading?.focus();
  }, [step]);

  function next(): void {
    if (canGoNext && step < LAST_STEP) {
      setStep(step + 1);
    }
  }

  function back(): void {
    if (step > STEP.welcome) {
      setStep(step - 1);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (event.key !== 'Enter' || isGate) {
      return;
    }
    const target = event.target;
    if (
      target instanceof HTMLInputElement &&
      (target.type === 'checkbox' || target.type === 'radio')
    ) {
      return;
    }
    if (
      target instanceof HTMLElement &&
      (target.tagName === 'BUTTON' ||
        target.tagName === 'TEXTAREA' ||
        target.closest('.wizard__add') !== null)
    ) {
      return;
    }
    event.preventDefault();
    next();
  }

  const stepProps: StepProps = { draft, setDraft, provider };

  return (
    <section className="wizard" aria-label="Newsletter setup" onKeyDown={handleKeyDown}>
      <div className="wizard__progress-row">
        <progress
          className="wizard__progress"
          max={LAST_STEP}
          value={step}
          aria-label="Setup progress"
        />
        <span className="wizard__progress-label">
          Step {step + 1} of {STEPS.length}
        </span>
      </div>

      <div className="wizard__screen" ref={titleRef}>
        {step === STEP.welcome && <WelcomeStep />}
        {step === STEP.purpose && <PurposeStep {...stepProps} />}
        {step === STEP.focus && <FocusStep {...stepProps} />}
        {step === STEP.voice && <VoiceStep {...stepProps} />}
        {step === STEP.name && <NameStep {...stepProps} />}
        {step === STEP.address && <AddressStep {...stepProps} onReadyChange={setAddressReady} />}
        {step === STEP.liked && <LikedStep {...stepProps} />}
        {step === STEP.building &&
          (signedIn ? (
            <SignedInGate draft={draft} onEdit={setStep} />
          ) : (
            <InlineSignup draft={draft} onEdit={setStep} />
          ))}
      </div>

      <div className="wizard__nav">
        {step > STEP.welcome && (
          <Button variant="ghost" onClick={back}>
            Back
          </Button>
        )}
        {!isGate && (
          <Button variant="primary" onClick={next} disabled={!canGoNext}>
            {step === STEP.welcome ? 'Start' : 'Next'}
          </Button>
        )}
      </div>
    </section>
  );
}

/** Exposed for tests / a future dashboard "start over" affordance. */
export function resetWizardDraft(draft: NewsletterDraft): void {
  clearDraft(draft.draftId);
}
